/**
 * Assessment Step Coverage Configuration — A10
 *
 * A v1 (step-based) auto-assessment scores whatever fraction of a Job happened
 * to occur inside the capture window. Phase 18.6 recomputes compliance over
 * observed steps only, so a minority-coverage clip produces a plausible score
 * with nothing recording the denominator. A10 measures that denominator,
 * persists it, surfaces it, and gates the automated consequences — it
 * deliberately does NOT block assessment creation, because partial coverage is
 * structural rather than exceptional (a 61-step Job summing 45.75 min of
 * expected duration cannot be more than ~33% covered by a 15-min clip).
 *
 * RULE 2 PARAMETRIC PATTERN:
 *   - Declaration: this file (static data only).
 *   - Dispatch:
 *       ENABLED + DISPATCH_FLOOR → Gate 0 in `dispatchRemediationBundle()`
 *         (`apps/api/src/services/training-distribution.service.ts`)
 *       ENABLED + WARN_BELOW     → the coverage banner + proficiency-claim
 *         branch in `apps/web/src/pages/assessments/AssessmentDetailPage.tsx`
 *   - Verification: `// PARAMETRIC-DISPATCH:` marker at each dispatch site.
 *
 * KEEP THIS FILE FREE OF IMPORTS AND `process.env` / `import.meta.env` READS.
 * It is written to be deep-importable from `apps/web`
 * (`@guild/config/assessment-coverage.config.js`) the same way
 * `nav-visibility.config.ts` is — the barrel `index.ts` transitively pulls
 * Node-only `process.env` reads, and `ai.config.ts` additionally imports from
 * `@google/genai`, both of which fail the web app's `tsc`.
 *
 * ⚠️ BOTH THRESHOLDS ARE PRIORS, NOT MEASUREMENTS. There is exactly one
 * post-Phase-18.6 assessment in production, so neither is calibrated against a
 * distribution. They are chosen to be tunable: the coverage ratio is logged and
 * persisted on every assessment from day one, so the pilot produces the
 * distribution that lets them be set properly. Revisit after ~2 weeks of
 * scheduled assessments. Do not cite these values as findings.
 */

export const ASSESSMENT_COVERAGE_CONFIG = {
  /**
   * Master kill switch. `false` restores exact pre-A10 dispatch behaviour and
   * suppresses the coverage banner + partial-coverage wording on the detail
   * page. The coverage tile is NOT gated by this — the raw observed/total
   * number is always shown when known.
   */
  ENABLED: true,

  /**
   * Below this observed/total ratio, `dispatchRemediationBundle()` suppresses
   * Ring 1 — and therefore Ring 2, which is enqueued inside Ring 1.
   *
   * Below half the Job observed you cannot attribute a low score to the
   * operator rather than to the capture window, and the cost of being wrong is
   * a paid Veo generation plus a cohort fan-out to same-shift peers.
   *
   * PRIOR, not measured — see the file header.
   */
  DISPATCH_FLOOR: 0.5,

  /**
   * Below this observed/total ratio, the assessment detail page withholds the
   * "meets proficiency requirements" claim and shows an amber banner.
   *
   * Deliberately HIGHER than DISPATCH_FLOOR: the rarer and more expensive
   * action gets the stricter bar. Between the two, a trainer sees a caveat and
   * remediation still fires. A proficiency claim asserts the operator did the
   * job — a false positive here costs a caption, a false negative costs a wrong
   * certification into the skill matrix, so this bar is the looser of the two.
   *
   * Compared with strict `<`, so a ratio of exactly WARN_BELOW shows no banner.
   *
   * PRIOR, not measured — see the file header.
   */
  WARN_BELOW: 0.75,
} as const;
